import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { TeamContext, TeamContextData } from '../../components/layouts/TeamLayout';
import Card from '../../components/common/Card';
import Skeleton from '../../components/common/Skeleton';
import AnimatedNumber from '../../components/common/AnimatedNumber';
import WeeklyLineupPanel from '../../components/team/WeeklyLineupPanel';

const TeamPerformance: React.FC = () => {
  const context = useContext(TeamContext) as TeamContextData;
  const { weeklyPerformance, team } = context;
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);

  // Get available weeks for navigation
  const availableWeeks = useMemo(() => {
    return weeklyPerformance?.weekly_performance
      ?.filter(week => week.total_points > 0 || week.matchup)
      ?.map(week => week.week)
      ?.sort((a, b) => a - b) || [];
  }, [weeklyPerformance]);

  // Keyboard navigation
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!selectedWeek || availableWeeks.length === 0) return;

    const currentIndex = availableWeeks.indexOf(selectedWeek);
    
    if (event.key === 'ArrowLeft' && currentIndex > 0) {
      event.preventDefault();
      setSelectedWeek(availableWeeks[currentIndex - 1]);
    } else if (event.key === 'ArrowRight' && currentIndex < availableWeeks.length - 1) {
      event.preventDefault();
      setSelectedWeek(availableWeeks[currentIndex + 1]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setSelectedWeek(null);
    }
  }, [selectedWeek, availableWeeks]);

  // Add/remove keyboard event listener
  useEffect(() => {
    if (selectedWeek) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedWeek, handleKeyDown]);

  if (!weeklyPerformance) {
    return (
      <Card className="p-6">
        <div className="space-y-4 py-4">
          <Skeleton className="h-8 w-56" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-xl font-bold text-text-primary mb-4">Week by Week</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold font-mono text-text-primary">
              <AnimatedNumber
                value={
                  weeklyPerformance.weekly_performance
                    .filter(week => week.starter_points > 0)
                    .reduce((sum, week) => sum + week.starter_points, 0) /
                  Math.max(
                    1,
                    weeklyPerformance.weekly_performance.filter(week => week.starter_points > 0).length
                  )
                }
              />
            </div>
            <div className="text-text-secondary">Avg Starter Points</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold font-mono text-accent-primary">
              <AnimatedNumber value={Number(weeklyPerformance.summary.best_week.points)} />
            </div>
            <div className="text-text-secondary">Best Week ({weeklyPerformance.summary.best_week.week})</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold font-mono text-accent-danger">
              <AnimatedNumber value={Number(weeklyPerformance.summary.worst_week.points)} />
            </div>
            <div className="text-text-secondary">Worst Week ({weeklyPerformance.summary.worst_week.week}) — we don't talk about it</div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-fieldline">
                <th className="text-left py-3 px-4 text-text-secondary font-medium">Week</th>
                <th className="text-right py-3 px-4 text-text-secondary font-medium">Score</th>
                <th className="text-right py-3 px-4 text-text-secondary font-medium">Opponent Score</th>
                <th className="text-right py-3 px-4 text-text-secondary font-medium">Projected Score</th>
                <th className="text-center py-3 px-4 text-text-secondary font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {weeklyPerformance.weekly_performance
                .filter(week => week.total_points > 0 || week.matchup)
                .map((week) => (
                <tr
                  key={week.week}
                  className="border-b border-fieldline hover:bg-background-hover cursor-pointer transition-colors"
                  onClick={() => setSelectedWeek(week.week)}
                >
                  <td className="py-3 px-4">
                    <span className="text-text-primary font-medium">Week {week.week}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-text-primary font-bold font-mono">
                      {Number(week.starter_points).toFixed(1)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-text-primary font-mono">
                      {week.matchup ? Number(week.matchup.opponent_score).toFixed(1) : '—'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-text-secondary font-mono">
                      {Number(week.projected_points).toFixed(1)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {week.matchup ? (
                      <span className={`inline-flex px-2 py-1 text-xs font-mono font-semibold rounded ${
                        week.matchup.result === 'win'
                          ? 'bg-accent-primary/15 text-accent-primary'
                          : 'bg-accent-danger/15 text-accent-danger'
                      }`}>
                        {week.matchup.result.toUpperCase()}
                      </span>
                    ) : (
                      <span className="text-text-tertiary text-xs">BYE</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Weekly Lineup Panel */}
      <WeeklyLineupPanel
        teamId={team?.id || 0}
        week={selectedWeek}
        teamName={team?.name}
        onClose={() => setSelectedWeek(null)}
      />
    </div>
  );
};

export default TeamPerformance;